// Distribution-domain list filters: merchandisers (PD-13923) and sensor
// devices (PD-13926). Both lists are backed by dedicated entities/endpoints
// (/api/merchandisers, /api/sensor-devices) rather than the generic object
// list, so their filters live here and are referenced by `activeFilters` in
// the corresponding list-builder configs.

import { createKeywordFilter, createStatusFilter } from './factories';
import type { FilterRegistry } from './types';

export const distributionFilters: FilterRegistry = {
	// --- Merchandiser list ---
	merchandiserSearch: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search merchandisers...',
			objectTable: 'merchandiser',
			listKeys: ['merchandiser'],
		}),
		queryParamName: 'search',
	},

	merchandiserStatus: {
		...createStatusFilter({
			label: 'Status',
			objectTable: 'merchandiser',
			fieldName: 'status',
			listKeys: ['merchandiser'],
			options: [
				{ id: 'active', label: 'Active' },
				{ id: 'inactive', label: 'Inactive' },
				{ id: 'maintenance', label: 'Maintenance' },
				{ id: 'offline', label: 'Offline' },
			],
		}),
		queryParamName: 'merchandiserStatus',
	},

	// Placement (PD-15560) — ONE combined typeahead over both places a unit can
	// sit: a warehouse (merchandiser.fk_warehouse__id) or a client location
	// (fk_client_address__id). The two are mutually exclusive on a row, so a
	// single filter reads better than two, and the option menu groups them under
	// headings from each option's `type` (see BaseMultiSelect). Option ids are
	// prefixed with their source (`warehouse:` / `clientAddress:`) so the
	// merchandisers endpoint can split the selection back apart — no
	// queryMapping, mirroring the other filters on this dedicated endpoint.
	merchandiserPlacement: {
		type: 'multi-select',
		label: 'Placement',
		placeholder: 'Search warehouses or client locations...',
		colorScheme: 'teal',
		searchable: true,
		loadOnFocus: true,
		menuMinWidth: 320,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
			secondary: 'subtitle' as keyof unknown,
			count: 'count' as keyof unknown,
		},
		apiEndpoint: '/merchandisers/placement/options',
		queryParamName: 'placement',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['merchandiser'],
	},

	// Make & Model — the manufacturer/model catalog pairs that exist on units,
	// as one combined typeahead ("Leer — Model 60 Indoor Glass"). Option ids
	// are `<manufacturerId>:<modelId>` pairs so the merchandisers endpoint
	// filters on the exact combination; no queryMapping, mirroring the other
	// filters on this dedicated endpoint.
	merchandiserModel: {
		type: 'multi-select',
		label: 'Make & Model',
		placeholder: 'Search make or model...',
		colorScheme: 'purple',
		searchable: true,
		loadOnFocus: true,
		menuMinWidth: 300,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
			secondary: 'subtitle' as keyof unknown,
			count: 'count' as keyof unknown,
		},
		apiEndpoint: '/merchandisers/model/options',
		queryParamName: 'makeModel',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['merchandiser'],
	},

	// Fill level (PD-12625) — the headline refill-planning filter on the
	// merchandiser map. Derived from the inventory rollup (stockLevel), so it
	// has no queryMapping; the map endpoint filters on the computed value.
	merchandiserFillLevel: {
		type: 'multi-select',
		label: 'Fill Level',
		placeholder: 'Select fill level...',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		options: [
			{ id: 'Empty', label: 'Empty' },
			{ id: 'Low', label: 'Low' },
			{ id: 'Medium', label: 'Medium' },
			{ id: 'Full', label: 'Full' },
		],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'fillLevel',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['merchandiser'],
	},

	// Health (PD-15733) — the managed sensor-driven state, derived from the
	// unit's open sensor alerts + device attachment (an open offline alert
	// wins, any other open alert needs attention, a sensored unit with a
	// clean slate is healthy, no sensor at all is its own state). Computed
	// server-side, so no queryMapping; the merchandisers endpoint reads the
	// `health` param and filters on the derived value.
	merchandiserHealth: {
		type: 'multi-select',
		label: 'Health',
		placeholder: 'Select health...',
		colorScheme: 'red',
		searchable: false,
		loadOnFocus: false,
		options: [
			{ id: 'healthy', label: 'Healthy' },
			{ id: 'needs_attention', label: 'Needs Attention' },
			{ id: 'offline', label: 'Offline' },
			{ id: 'no_sensors', label: 'No Sensors' },
		],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'health',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['merchandiser'],
	},

	// High temperature (PD-13938) — derived quick-filter over the temperature
	// bubbled up from the attached sensor device
	// (merchandiser.currentTemperature). Single-option: when set, the
	// merchandisers backend reads the `highTemp` param and applies
	// currentTemperature >= MERCHANDISER_HIGH_TEMP_THRESHOLD (the
	// team-tunable constant in @supply-mover-2-0/shared). No queryMapping —
	// mirrors deviceFilter below.
	merchandiserHighTemp: {
		type: 'single-select',
		label: 'Temperature',
		placeholder: 'Any temperature',
		colorScheme: 'red',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [{ id: 'high', label: 'High Temp' }],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'highTemp',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['merchandiser'],
	},

	// Needs Refill (PD-16059) — units at or below the tenant's refill
	// threshold (`refillBelowFillPct`, Distribution settings). Derived from
	// the inventory rollup server-side, so no queryMapping; the option id is
	// the literal `true` the merchandisers list and map endpoints read as
	// `refillNeeded=true`. Drives the Needs Refill tab; fill-level buckets
	// stay a separate, display-only filter.
	merchandiserRefillNeeded: {
		type: 'single-select',
		label: 'Refill',
		placeholder: 'Any fill',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [{ id: 'true', label: 'Needs Refill' }],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'refillNeeded',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['merchandiser'],
	},

	// --- Merchandiser template list (PD-15559) ---
	merchandiserTemplateSearch: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Template name or description...',
			objectTable: 'merchandiser_template',
			listKeys: ['merchandiser_template'],
		}),
		queryParamName: 'search',
	},

	// A template is either in use or retired. The list defaults to Active
	// (see MERCHANDISER_TEMPLATE_LIST_CONFIG.filters.defaultFilters) because a
	// retired setup is history rather than something an operator is choosing
	// from; clearing the chip shows both. isActive is a real column, but the
	// endpoint reads the chip as `activeFilter` and applies it itself, so
	// there is no queryMapping (mirrors deviceFilter above).
	merchandiserTemplateActive: {
		type: 'single-select',
		label: 'Status',
		placeholder: 'All templates',
		colorScheme: 'green',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [
			{ id: 'active', label: 'Active' },
			{ id: 'inactive', label: 'Retired' },
		],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'activeFilter',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['merchandiser_template'],
	},

	// --- Sensor device list ---
	sensorDeviceSearch: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Device ID, name, merchandiser...',
			objectTable: 'sensor_device',
			listKeys: ['sensor_device'],
		}),
		queryParamName: 'search',
	},

	// --- Device reading log (PD-15734, merchandiser/device detail) ---
	// Timeframe over the reading timestamp. The value is the standard
	// date-range shape ({startDate, endDate}); the dedicated readings
	// endpoint applies it as from/to, so there is no queryMapping.
	sensorReadingTimeframe: {
		type: 'date-range',
		label: 'Timeframe',
		placeholder: 'Select date range...',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		displayFields: {
			id: 'value' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'timeframe',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'data',
		},
		listKeys: ['sensorReading-device-embedded'],
	},

	// Module selector for the device reading log. Devices carry up to 8
	// addressable modules; readings for module numbers a device doesn't have
	// simply don't exist, so over-offering is harmless. Resolved by the
	// dedicated endpoint (moduleId param) — no queryMapping.
	sensorReadingModule: {
		type: 'single-select',
		label: 'Module',
		placeholder: 'All modules',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [
			{ id: '1', label: 'Module 1' },
			{ id: '2', label: 'Module 2' },
			{ id: '3', label: 'Module 3' },
			{ id: '4', label: 'Module 4' },
			{ id: '5', label: 'Module 5' },
			{ id: '6', label: 'Module 6' },
			{ id: '7', label: 'Module 7' },
			{ id: '8', label: 'Module 8' },
		],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'moduleId',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['sensorReading-device-embedded'],
	},

	// Derived quick-filter — NOT a single column. "Needs Attention" is a
	// compound health condition (offline/error/stale/low-battery), "Has
	// Orphans" is a post-count comparison (assigned < total), and
	// "Unattached" is merchandiserId IS NULL. So there is no queryMapping:
	// the sensor-devices backend reads the `deviceFilter` param and applies
	// the condition itself (mirrors vehicleTripStatus).
	deviceFilter: {
		type: 'single-select',
		label: 'Show',
		placeholder: 'All devices',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [
			{ id: 'needs_attention', label: 'Needs Attention' },
			{ id: 'has_orphans', label: 'Has Orphans' },
			{ id: 'unattached', label: 'Unattached' },
		],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'deviceFilter',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['sensor_device'],
	},

	// --- Replenishment (PD-16211) ---
	// The list comes from ReplenishmentListService rather than an entity, so
	// none of these carry a queryMapping: the service reads the keys off the
	// request itself, exactly as the Registry list does.

	replenishmentRoute: {
		type: 'multi-select',
		label: 'Route',
		placeholder: 'Search routes...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
		},
		apiEndpoint: '/routes/options',
		queryParamName: 'replenishmentRoute',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['replenishment'],
	},

	// At or below the tenant's refill threshold (Settings -> Distribution),
	// the same number the Merchandisers list calls Needs Refill.
	replenishmentBelowThreshold: {
		type: 'single-select',
		label: 'Fill',
		placeholder: 'Any fill',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [{ id: 'true', label: 'Below refill threshold' }],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'replenishmentBelowThreshold',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['replenishment'],
	},

	// A customer whose sensors are down has stale numbers, so the suggestion
	// beside them is worth treating differently.
	replenishmentSensorDown: {
		type: 'single-select',
		label: 'Sensors',
		placeholder: 'Any sensor state',
		colorScheme: 'red',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [{ id: 'true', label: 'Sensors down' }],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'replenishmentSensorDown',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['replenishment'],
	},

	// --- Trip builder accounts (PD-16532) ---
	// Read by DispatchCandidatesService off the filter blob, like the
	// Replenishment filters above; no queryMapping.

	// PD-16956 (F-37) — find an account by name or address in the trip
	// builder. The list had no search: on a desktop there was no box at all,
	// and the phone's box did nothing because no text filter was registered.
	// The builder's query sends the term as the endpoint's `search`.
	dispatchSearch: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search by name or address...',
			objectTable: 'client',
			listKeys: ['dispatch_candidate', 'dispatch_service_candidate'],
		}),
		queryParamName: 'dispatchSearch',
	},

	dispatchRoute: {
		type: 'multi-select',
		label: 'Route',
		placeholder: 'Search routes...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
		},
		apiEndpoint: '/routes/options',
		queryParamName: 'dispatchRoute',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['dispatch_candidate', 'dispatch_service_candidate'],
	},

	// Need score 50 or more: due today, or a cooler half empty.
	dispatchNeedsIce: {
		type: 'single-select',
		label: 'Need',
		placeholder: 'Any need',
		colorScheme: 'red',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [{ id: 'true', label: 'Needs ice' }],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'dispatchNeedsIce',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['dispatch_candidate'],
	},

	// An approved order not on any trip yet.
	dispatchHasOrder: {
		type: 'single-select',
		label: 'Waiting',
		placeholder: 'Any',
		colorScheme: 'green',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [{ id: 'true', label: 'Has an order waiting' }],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'dispatchHasOrder',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['dispatch_candidate'],
	},

	// PD-16114 — an approved order waiting whose delivery date is on or
	// before the trip's day (or that has none): what "Add to trip" attaches.
	dispatchDueByTripDate: {
		type: 'single-select',
		label: 'Order due',
		placeholder: 'Any',
		colorScheme: 'green',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [{ id: 'true', label: 'Order due by the trip date' }],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'dispatchDueByTripDate',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['dispatch_candidate'],
	},

	// PD-16114 — a merchandiser drop-off or pick-up due by the trip date
	// (the same rule as "Order due"): later equipment work is not this trip's.
	dispatchEquipmentWaiting: {
		type: 'single-select',
		label: 'Drop-off / pick-up',
		placeholder: 'Any',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [{ id: 'true', label: 'Drop-off or pick-up due by the trip date' }],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'dispatchEquipmentWaiting',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['dispatch_candidate'],
	},

	// Forecast due by the trip's date plus this many days (0 = overdue).
	dispatchDueWithinDays: {
		type: 'single-select',
		label: 'Due',
		placeholder: 'Any time',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [{ id: '0', label: 'Overdue' }, { id: '3', label: 'Within 3 days' }, { id: '7', label: 'Within 7 days' }],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'dispatchDueWithinDays',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['dispatch_candidate'],
	},

	// Counted from the trip's date; never delivered counts.
	dispatchNotDeliveredDays: {
		type: 'single-select',
		label: 'Not delivered in',
		placeholder: 'Any',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [{ id: '7', label: '7+ days' }, { id: '14', label: '14+ days' }, { id: '30', label: '30+ days' }],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'dispatchNotDeliveredDays',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['dispatch_candidate'],
	},

	// At or below the tenant's refill threshold, as on the Merchandisers list.
	dispatchBelowThreshold: {
		type: 'single-select',
		label: 'Fill',
		placeholder: 'Any fill',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [{ id: 'true', label: 'Below refill threshold' }],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'dispatchBelowThreshold',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['dispatch_candidate'],
	},

	// Stale numbers: worth a look before trusting the need.
	dispatchSensorDown: {
		type: 'single-select',
		label: 'Sensors',
		placeholder: 'Any sensor state',
		colorScheme: 'red',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [{ id: 'true', label: 'Sensors down' }],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'dispatchSensorDown',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['dispatch_candidate', 'dispatch_service_candidate'],
	},
	// --- Service-trip builder accounts (PD-16797) ---

	// An open service order not on a trip (open, or partly done and back).
	dispatchHasServiceOrder: {
		type: 'single-select',
		label: 'Service waiting',
		placeholder: 'Any',
		colorScheme: 'green',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [{ id: 'true', label: 'Has a service order waiting' }],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'dispatchHasServiceOrder',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['dispatch_service_candidate'],
	},

	// An open service order dated on or before the trip's day.
	dispatchServiceDue: {
		type: 'single-select',
		label: 'Service due',
		placeholder: 'Any',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [{ id: 'true', label: 'Service due by the trip date' }],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'dispatchServiceDue',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['dispatch_service_candidate'],
	},

	// Counted from the trip's date; never serviced counts.
	dispatchNotServicedDays: {
		type: 'single-select',
		label: 'Last service',
		placeholder: 'Any',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [
			{ id: '30', label: 'Not serviced in 30+ days' },
			{ id: '90', label: 'Not serviced in 90+ days' },
			{ id: '180', label: 'Not serviced in 180+ days' },
		],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'dispatchNotServicedDays',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['dispatch_service_candidate'],
	},

	// PD-16114 — the Schedule: what each order carries. An order can carry
	// several (an event's delivery day has ice and drop-offs). PD-16797 —
	// Service lists open service orders, the Schedule's second source.
	scheduleType: {
		type: 'multi-select',
		label: 'Type',
		placeholder: 'All types',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [
			{ id: 'delivery', label: 'Deliveries' },
			{ id: 'drop_off', label: 'Drop-offs' },
			{ id: 'pick_up', label: 'Pick-ups' },
			{ id: 'service', label: 'Service' },
		],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'scheduleType',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['distribution_schedule'],
	},

	// PD-16114 — by default the Schedule lists what still needs a trip; this
	// also shows orders already on one, with their trip.
	scheduleIncludeOnTrip: {
		type: 'single-select',
		label: 'On a trip',
		placeholder: 'Waiting only',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		allowClear: true,
		options: [{ id: 'true', label: 'Include work already on a trip' }],
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'scheduleIncludeOnTrip',
		multiple: false,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['distribution_schedule'],
	},
};
